/* global analytics */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import ahoy from 'ahoy.js'
import { v4 as uuid } from 'uuid'

import useDispatch from 'hooks/useDispatch'
import useHideSiteFooter from 'hooks/useHideSiteFooter'
import useHideIntercom from 'hooks/useHideIntercom'
import useLatestDefined from 'hooks/useLatestDefined'
import { useSearchQuery } from 'hooks/useQueries'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { dispatchRollbarError } from 'utils/rollbar'
import { useUtmParams } from 'utils/utmParams'
import { staysAdventuresDropdown } from 'featureFlags'

import GlobalLoader from '../components/display/GlobalLoader'
import MetaTags from '../components/meta/MetaTags'
import JsonLd from '../components/utils/JsonLd'
import { searchResultsPageJsonLd } from '../components/utils/structuredData'

import SearchMap from './Map'
import Results from './Results'
import DesktopSearchView from './DesktopSearchView'
import MobileSearchView from './MobileSearchView'
import SearchTypeToggle, { SearchType } from './SearchTypeToggle'
import SearchTypeDropdown from './SearchTypeDropdown'

import {
  deserializeSearchQuery,
  pathnameForSearchType,
} from '../actions/utils/search'
import { updateFilters } from '../actions/searchActions'

import useWhichMapMode from '../components/layout/ItemGridWithMap/useWhichMapMode'

import useScrollTop from './useSearchScrollTop'
import useDriveTimeFilterAvailable from './useDriveTimeFilterAvailable'
import useDetectedSearchLocation from './useDetectedSearchLocation'
import { SEARCH_PAGE_SIZE, SEARCH_FETCH_MORE_SIZE } from './constants'

type LocationState = {
  primaryCityName: string
}

type SeoState = {
  rails: {
    data: {
      seoH1?: string | null
      seoTitle?: string | null
      seoDescription?: string | null
      searchResults?: unknown
    } | null
  }
}

// Server-rendered SEO heading. Visually hidden so it doesn't disrupt the
// search UI but still present in the SSR markup for crawlers.
const VisuallyHiddenH1 = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`

const dispatchAnalyticsEvent = (
  resultsCount: number,
  filters: GQL_SearchFilters,
  locationState: LocationState,
  utmParams: Record<string, string> = {},
  driveTimeFilterAvailable?: boolean,
) => {
  const properties = {
    searchId: uuid(),
    resultsCount,
    ...filters,
    ...(locationState?.primaryCityName
      ? { primary_city: locationState.primaryCityName }
      : {}),
    ...utmParams,
    // Exposure marker for the drive-time experiment: eligible vs not,
    // independent of whether the filter was used (which `driveTime` carries)
    driveTimeFilterAvailable,
  }

  analytics.track('Search', properties)
  ahoy.track('Search', properties)
}

const SearchView = () => {
  useHideIntercom()
  const dispatch = useDispatch()
  const {
    search: searchQuery,
    pathname,
    state: locationState,
  } = useLocation<LocationState>()
  const seoH1 = useSelector(
    (state: SeoState) => state.rails?.data?.seoH1 ?? null,
  )
  const seoTitle = useSelector(
    (state: SeoState) => state.rails?.data?.seoTitle ?? null,
  )
  const seoDescription = useSelector(
    (state: SeoState) => state.rails?.data?.seoDescription ?? null,
  )

  useScrollTop()

  useHideSiteFooter()

  const mapMode = useWhichMapMode()
  const inlineMapMode = mapMode === 'inline'

  // Derived purely from the URL; memoised so re-renders don't hand filter
  // components a new object and wipe their in-progress selection.
  const filters: GQL_SearchFilters = useMemo(
    () => deserializeSearchQuery(searchQuery, pathname),
    [searchQuery, pathname],
  )

  const [searchTypeToggleOpen, setSearchTypeToggleOpen] = useState(false)

  const { type: currentSearchType } = filters

  const hasDropdownFeatureFlag = useFeatureFlag(staysAdventuresDropdown)

  const setPrimaryFilterType = (type: SearchType) => {
    dispatch(
      updateFilters(
        {
          type,
          // reset all accommodation-specific filters
          adults: null,
          children: null,
          amenities: null,
          startDate: null,
          weekendMonths: null,
          endDate: null,
          petFriendly: null,
          priceStart: null,
          priceEnd: null,
          subcategories: null,
          instantBook: null,
          // reset all adventure-specific filters
          adventureTypes: null,
        },
        pathnameForSearchType(type),
      ),
    )
    setSearchTypeToggleOpen(false)
  }

  const utmParams = useUtmParams()

  const {
    willDetect,
    settled: detectionSettled,
    appliedSearch,
  } = useDetectedSearchLocation()

  // 24 only for the search the server rendered for this page load (matches the
  // seeded cache key); a later in-page search isn't crawled, so it stays small.
  // The detected-location swap counts as that same page load's search.
  const initialSearchQuery = useRef(searchQuery).current
  const pageSize =
    searchQuery === initialSearchQuery || searchQuery === appliedSearch
      ? SEARCH_PAGE_SIZE
      : SEARCH_FETCH_MORE_SIZE

  const hasSeed = useSelector(
    (state: SeoState) => state.rails?.data?.searchResults != null,
  )
  const holdInitialQuery = willDetect && !hasSeed && !detectionSettled

  const { loading, data, error, fetchMore } = useSearchQuery({
    variables: {
      pageSize,
      params: filters,
    },
    /* cache-first is apollo's default, but we want it explicit because if the user navigates away and then back
    to search results, hitting the cache is essential for maintaining their scroll/pagination position */
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: holdInitialQuery,
  })

  // Apollo's `skip` reports loading: false, so the held state looks finished.
  const searchLoading = loading || holdInitialQuery

  const driveTimeFilterState = useDriveTimeFilterAvailable(filters)

  // Track a 'Search' analytics event once per completed search. Keyed on the
  // filters signature (deduped via ref) so pagination via fetchMore — which
  // changes `data` but not `filters` — does not re-fire the event. Held until
  // drive-time eligibility resolves so the exposure marker is never unknown
  // (flag-off visitors resolve immediately).
  const lastTrackedSearch = useRef<string | null>(null)
  useEffect(() => {
    if (searchLoading || !driveTimeFilterState.resolved || !detectionSettled)
      return
    const resultsCount = data?.search?.results?.totalCount
    if (resultsCount == null) return
    const searchSignature = JSON.stringify(filters)
    if (lastTrackedSearch.current === searchSignature) return
    lastTrackedSearch.current = searchSignature
    dispatchAnalyticsEvent(
      resultsCount,
      filters,
      locationState,
      utmParams,
      driveTimeFilterState.available,
    )
  }, [
    searchLoading,
    detectionSettled,
    data,
    filters,
    locationState,
    utmParams,
    driveTimeFilterState,
  ])

  useEffect(() => {
    if (error?.graphQLErrors?.length > 0) {
      dispatchRollbarError(error)
    }
  }, [error])

  const fetchMoreResults = () => {
    fetchMore({
      variables: {
        pageSize: SEARCH_FETCH_MORE_SIZE,
        cursor: data.search.results.pageInfo.endCursor,
      },
    })
  }

  const defaults = useLatestDefined(data?.search?.defaults)
  const results = data?.search?.results

  const searchTypeToggleComponent = hasDropdownFeatureFlag ? (
    <SearchTypeDropdown
      setPrimaryFilterType={setPrimaryFilterType}
      currentSearchType={currentSearchType}
    />
  ) : (
    <SearchTypeToggle
      open={searchTypeToggleOpen}
      setOpen={(nextOpen) => setSearchTypeToggleOpen(nextOpen)}
      setPrimaryFilterType={setPrimaryFilterType}
      currentSearchType={currentSearchType}
    />
  )

  const resultsComponent = (
    <Results
      filters={filters}
      loading={searchLoading}
      defaults={defaults}
      results={results}
      fetchMore={fetchMoreResults}
      isDesktop={inlineMapMode}
    />
  )

  const mapComponent = (
    <SearchMap filters={filters} searchQuery={searchQuery} mapMode={mapMode} />
  )

  return (
    <>
      <JsonLd
        data={searchResultsPageJsonLd(
          results?.edges?.map((edge) => edge.node.result),
          { name: seoH1 ?? seoTitle },
        )}
      />
      {seoTitle && (
        <MetaTags title={seoTitle} description={seoDescription ?? undefined} />
      )}
      {seoH1 && <VisuallyHiddenH1>{seoH1}</VisuallyHiddenH1>}
      <div>
        <GlobalLoader loading={searchLoading} />

        {inlineMapMode ? (
          <DesktopSearchView
            filters={filters}
            defaults={defaults}
            minPrice={results?.minPrice}
            maxPrice={results?.maxPrice}
            searchTypeToggleComponent={searchTypeToggleComponent}
            resultsComponent={resultsComponent}
            mapComponent={mapComponent}
          />
        ) : (
          <MobileSearchView
            filters={filters}
            defaults={defaults}
            minPrice={results?.minPrice}
            maxPrice={results?.maxPrice}
            searchTypeToggleComponent={searchTypeToggleComponent}
            resultsComponent={resultsComponent}
            mapComponent={mapComponent}
            resultsCount={results?.totalCount}
          />
        )}
      </div>
    </>
  )
}

export default SearchView
